import {
  Radio as ReactAriaRadio,
  type RadioProps as ReactAriaRadioProps,
  composeRenderProps,
} from "react-aria-components";

import { customTv } from "#utils";

const radioStyles = customTv({
  // `relative` is required so the visually-hidden <input> rendered by
  // react-aria-components (which uses `position: absolute`) is positioned
  // relative to this label rather than the document root. Without it,
  // clicking the radio can scroll the page out of view when the
  // radio is nested inside a scrollable container (e.g. a table).
  base: "relative flex items-center gap-2 text-label-medium transition",
  variants: {
    isDisabled: {
      true: "text-text-disabled cursor-not-allowed",
    },
  },
});

const circleStyles = customTv({
  base: "shrink-0 flex items-center justify-center transition w-4 h-4 rounded-4 bg-neutral-white border border-outline-input-enabled",
  variants: {
    isSelected: {
      true: "!border-[5px] border-outline-brand",
      false: "",
    },
    isDisabled: {
      true: "border-outline-input-disabled",
      false: "",
    },
    isFocusVisible: {
      true: "border-2 border-outline-brand",
      false: "",
    },
    isHovered: {
      true: "border-outline-input-hover",
      false: "",
    },
  },
  compoundVariants: [
    {
      isSelected: true,
      isHovered: true,
      class: "border-interactive-primary-button-hover-background",
    },
    {
      isSelected: true,
      isFocusVisible: true,
      class: "border-interactive-primary-button-focus-background",
    },
    {
      isDisabled: true,
      isHovered: true,
      class: "border-outline-input-disabled",
    },
    {
      isSelected: true,
      isDisabled: true,
      class:
        "border-interactive-secondary-button-focus-background bg-interactive-secondary-button-hover-background",
    },
    {
      isSelected: true,
      isDisabled: true,
      isHovered: true,
      class: "border-interactive-secondary-button-focus-background",
    },
  ],
});

export interface RadioProps extends ReactAriaRadioProps {
  className?: string;
}

export function Radio({ className, ...props }: RadioProps) {
  return (
    <ReactAriaRadio
      className={composeRenderProps(className, (className, renderProps) =>
        radioStyles({ ...renderProps, className }),
      )}
      {...props}
    >
      {(renderProps) => (
        <>
          <div
            className={circleStyles({
              isSelected: renderProps.isSelected,
              isDisabled: renderProps.isDisabled,
              isFocusVisible: renderProps.isFocusVisible,
              isHovered: renderProps.isHovered,
            })}
          />
          {props.children}
        </>
      )}
    </ReactAriaRadio>
  );
}
